const mobileViewport = window.matchMedia("(max-width: 639px)")
// requirement-reduced-motion (overlay.modal) is a `must`: the preference
// removes translation and scaling while keeping a near-instant visibility
// change. The gesture still recognises the pull and still dismisses - only the
// sheet's movement is dropped, so dismissal stays reachable either way.
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
const interactiveSelector =
  "a, button, input, select, textarea, [contenteditable], [role='button']"

export const setupMobileSheetGesture = (sheet) => {
  if (!sheet || sheet.dataset.modalMobilePresentation !== "sheet")
    return () => {}

  const dragRegion =
    sheet.querySelector("[data-section-region='header']") ||
    sheet.querySelector(".details-modal-header")

  if (!dragRegion) return () => {}

  let drag = null
  let pendingDismiss = null

  const clearPendingDismiss = () => {
    if (!pendingDismiss) return
    window.removeEventListener("phoenix.link.click", pendingDismiss)
    pendingDismiss = null
  }

  const resetPosition = () => {
    if (reducedMotion.matches) {
      sheet.style.removeProperty("transition")
      sheet.style.removeProperty("transform")
      return
    }

    sheet.style.transition = "transform 180ms cubic-bezier(0.2, 0, 0, 1)"
    sheet.style.transform = "translateY(0)"

    window.setTimeout(() => {
      if (!sheet.isConnected) return
      sheet.style.removeProperty("transition")
      sheet.style.removeProperty("transform")
    }, 180)
  }

  const onTouchStart = (event) => {
    if (
      !mobileViewport.matches ||
      event.touches.length !== 1 ||
      event.target.closest(interactiveSelector) ||
      sheet.scrollTop > 0
    ) {
      return
    }

    const touch = event.touches[0]
    drag = {
      touchId: touch.identifier,
      startTime: performance.now(),
      startY: touch.clientY,
      distance: 0,
    }
    sheet.style.transition = "none"
  }

  const onTouchMove = (event) => {
    if (!drag) return

    const touch = Array.from(event.touches).find(
      ({ identifier }) => identifier === drag.touchId,
    )
    if (!touch) return

    const distance = touch.clientY - drag.startY
    if (distance < 0 || sheet.scrollTop > 0) {
      drag = null
      sheet.style.removeProperty("transition")
      sheet.style.removeProperty("transform")
      return
    }

    drag.distance = distance
    if (!reducedMotion.matches) {
      sheet.style.transform = `translateY(${drag.distance}px)`
    }
    event.preventDefault()
  }

  const finishDrag = (event, cancelled = false) => {
    if (
      !drag ||
      !Array.from(event.changedTouches).some(
        ({ identifier }) => identifier === drag.touchId,
      )
    ) {
      return
    }

    const elapsed = Math.max(performance.now() - drag.startTime, 1)
    const velocity = drag.distance / elapsed
    const distanceThreshold = Math.min(120, sheet.clientHeight * 0.25)
    const shouldDismiss =
      !cancelled &&
      (drag.distance >= distanceThreshold ||
        (drag.distance >= 24 && velocity >= 0.5))

    drag = null

    if (!shouldDismiss) {
      resetPosition()
      return
    }

    if (!reducedMotion.matches) {
      sheet.style.transition = "transform 220ms cubic-bezier(0.2, 0, 0, 1)"
      sheet.style.transform = "translateY(100dvh)"
    }

    const dismiss =
      sheet.querySelector("[data-modal-dismiss]") ||
      sheet.querySelector("[data-role='modal-close-button']")

    if (!dismiss) {
      resetPosition()
      return
    }

    clearPendingDismiss()
    pendingDismiss = (event) => {
      if (event.target !== dismiss) return
      clearPendingDismiss()
      if (event.defaultPrevented) resetPosition()
    }
    window.addEventListener("phoenix.link.click", pendingDismiss)
    dismiss.click()

    // A host that hides its sheet by toggling a class rather than unmounting it
    // keeps this element alive, so `destroyed()` never runs and the off-screen
    // transform would survive into the next open. Clear it once the dismissal
    // animation is done and the sheet is hidden, unless a new drag has started.
    window.setTimeout(() => {
      if (drag) return
      sheet.style.removeProperty("transition")
      sheet.style.removeProperty("transform")
    }, 260)
  }

  const onTouchEnd = (event) => finishDrag(event)
  const onTouchCancel = (event) => finishDrag(event, true)

  dragRegion.addEventListener("touchstart", onTouchStart)
  dragRegion.addEventListener("touchmove", onTouchMove, { passive: false })
  dragRegion.addEventListener("touchend", onTouchEnd)
  dragRegion.addEventListener("touchcancel", onTouchCancel)

  return () => {
    dragRegion.removeEventListener("touchstart", onTouchStart)
    dragRegion.removeEventListener("touchmove", onTouchMove)
    dragRegion.removeEventListener("touchend", onTouchEnd)
    dragRegion.removeEventListener("touchcancel", onTouchCancel)
    clearPendingDismiss()
    sheet.style.removeProperty("transition")
    sheet.style.removeProperty("transform")
  }
}
